import { redactSensitiveData } from './sentry-redact'

let activeSentry = null

/**
 * Browser-side Sentry / GlitchTip init for the marketing site.
 *
 * Lazy-imported so a missing @sentry/react package or unset
 * VITE_SENTRY_DSN both degrade silently - the site boots even before
 * the dep or self-hosted GlitchTip DSN lands.
 *
 * Mirrors claimguru-app's src/lib/sentry.js so all 4 browser surfaces
 * tell the same story.
 *
 * Env (Vite):
 *   VITE_SENTRY_DSN          : required. If unset, observability is OFF.
 *   VITE_SENTRY_ENVIRONMENT  : defaults to import.meta.env.MODE.
 *   VITE_SENTRY_TRACES_RATE  : 0..1, default 0.1 prod / 1.0 dev.
 *   VITE_SENTRY_RELEASE      : git sha, set by deploy hook.
 */
export async function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    activeSentry = null
    if (import.meta.env.MODE !== 'test') {
      console.warn('[sentry] VITE_SENTRY_DSN not set: observability disabled')
    }
    return null
  }

  let Sentry
  try {
    Sentry = await import('@sentry/react')
  } catch (err) {
    activeSentry = null
    console.warn('[sentry] @sentry/react not installed: observability disabled. Run pnpm install.')
    return null
  }

  const env = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development'
  const tracesSampleRate = (() => {
    const raw = import.meta.env.VITE_SENTRY_TRACES_RATE
    if (raw != null && raw !== '') {
      const n = Number(raw)
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n
    }
    return env === 'production' ? 0.1 : 1.0
  })()

  Sentry.init({
    dsn,
    environment: env,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration?.()].filter(Boolean),
    beforeSend(event) {
      return redactSensitiveData(event)
    },
    beforeBreadcrumb(breadcrumb) {
      if (
        breadcrumb.category === 'console' &&
        typeof breadcrumb.message === 'string' &&
        /(?:authorization|bearer|cookie|x-clerk)/i.test(breadcrumb.message)
      ) {
        return null
      }
      return breadcrumb
    },
  })

  activeSentry = Sentry
  return Sentry
}

/**
 * Report an error surfaced through React 19's createRoot callbacks.
 * React 19 stops rethrowing ErrorBoundary-caught errors to window.onerror,
 * so createRoot must wire onUncaughtError, onCaughtError, and
 * onRecoverableError explicitly.
 */
export function reportRootError(error, errorInfo, kind = 'uncaught', sink = activeSentry) {
  const componentStack = errorInfo?.componentStack || undefined
  if (sink && typeof sink.captureException === 'function') {
    try {
      sink.captureException(error, {
        level: kind === 'recoverable' ? 'warning' : 'error',
        tags: { 'react.error_hook': kind },
        contexts: componentStack ? { react: { componentStack } } : undefined,
      })
    } catch (telemetryErr) {
      console.warn('[sentry] captureException failed:', telemetryErr)
    }
  }
  if (kind === 'recoverable') {
    console.warn('[react:recoverable]', error)
  } else {
    console.error(`[react:${kind}]`, error)
  }
}

export function createRootErrorOptions({ sink } = {}) {
  return {
    onUncaughtError(error, errorInfo) {
      reportRootError(error, errorInfo, 'uncaught', sink ?? activeSentry)
    },
    onCaughtError(error, errorInfo) {
      reportRootError(error, errorInfo, 'caught', sink ?? activeSentry)
    },
    onRecoverableError(error, errorInfo) {
      reportRootError(error, errorInfo, 'recoverable', sink ?? activeSentry)
    },
  }
}
